/**
 * 공지사항 목록 페이지
 *
 * 고정 공지를 상단에 정렬하고, 그 안에서 최신순으로 나열한다.
 * 제목·내용 텍스트 검색 및 고정/공개/비공개 필터를 지원한다.
 */
import { useQuery } from "@tanstack/react-query";
import { noticeService } from "../../services/noticeService";
import { Link } from "react-router-dom";
import { useState } from "react";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import StatsCard from "../../components/ui/StatsCard";
import { formatDate } from "../../utils/format";

export default function NoticeList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "pinned" | "visible" | "hidden"
  >("all");

  const { data: notices, isLoading, error } = useQuery({
    queryKey: ["notices"],
    queryFn: noticeService.getAllNotices,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert />;

  const filteredNotices = (
    notices?.filter((notice) => {
      const matchesSearch =
        notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notice.content.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterStatus === "all" ||
        (filterStatus === "pinned" && notice.isPinned) ||
        (filterStatus === "visible" && notice.isVisible) ||
        (filterStatus === "hidden" && !notice.isVisible);
      return matchesSearch && matchesFilter;
    }) || []
  ).sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const totalCount = notices?.length || 0;
  const pinnedCount = notices?.filter((n) => n.isPinned).length || 0;
  const visibleCount = notices?.filter((n) => n.isVisible).length || 0;
  const hiddenCount = notices?.filter((n) => !n.isVisible).length || 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">공지사항 관리</h1>
          <p className="text-gray-500 mt-2">앱 공지사항을 관리합니다</p>
        </div>
        <Link
          to="/admin/notice/new"
          className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-md flex items-center space-x-2"
        >
          <i className="fas fa-plus"></i>
          <span>공지사항 작성</span>
        </Link>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatsCard label="전체 공지" value={totalCount} icon="fas fa-bullhorn" borderColor="border-primary" iconColor="text-primary" bgColor="bg-primary/10" />
        <StatsCard label="고정 공지" value={pinnedCount} icon="fas fa-thumbtack" borderColor="border-marker-orange" iconColor="text-marker-orange" bgColor="bg-marker-orange/10" valueColor="text-marker-orange" />
        <StatsCard label="공개" value={visibleCount} icon="fas fa-eye" borderColor="border-green-500" iconColor="text-green-500" bgColor="bg-green-500/10" valueColor="text-green-600" />
        <StatsCard label="비공개" value={hiddenCount} icon="fas fa-eye-slash" borderColor="border-gray-400" iconColor="text-gray-400" bgColor="bg-gray-400/10" valueColor="text-gray-600" />
      </div>

      {/* Search & Filter Bar */}
      <div className="mb-6 bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="제목, 내용으로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-light outline-none transition-all"
            />
          </div>
          <div className="relative">
            <i className="fas fa-filter absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-light outline-none transition-all appearance-none bg-white cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1.5em 1.5em",
                backgroundRepeat: "no-repeat",
              }}
            >
              <option value="all">전체 공지사항</option>
              <option value="pinned">고정 공지만</option>
              <option value="visible">공개 공지만</option>
              <option value="hidden">비공개 공지만</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notice List */}
      <div className="space-y-4">
        {filteredNotices.length > 0 ? (
          filteredNotices.map((notice) => (
            <Link
              key={notice.id}
              to={`/admin/notice/${notice.id}`}
              state={{ notice }}
              className="block bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-100 hover:border-primary/30"
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {notice.isPinned && (
                        <span className="inline-flex items-center px-3 py-1.5 bg-marker-orange text-white text-sm font-bold rounded-lg shadow-sm">
                          <i className="fas fa-thumbtack mr-1.5"></i>고정
                        </span>
                      )}
                      {notice.isVisible ? (
                        <span className="inline-flex items-center px-3 py-1.5 bg-green-500 text-white text-sm font-bold rounded-lg shadow-sm">
                          <i className="fas fa-eye mr-1.5"></i>공개
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1.5 bg-gray-500 text-white text-sm font-bold rounded-lg shadow-sm">
                          <i className="fas fa-eye-slash mr-1.5"></i>비공개
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 group-hover:text-primary transition-colors leading-tight">
                      {notice.title}
                    </h3>
                  </div>
                  <div className="ml-4">
                    <i className="fas fa-chevron-right text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all text-xl"></i>
                  </div>
                </div>
                <p className="text-gray-600 text-base line-clamp-2 mb-4 leading-relaxed">
                  {notice.content}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <span className="flex items-center space-x-2">
                      <i className="fas fa-calendar-plus text-primary"></i>
                      <span className="font-medium">
                        작성: {formatDate(notice.createdAt)}
                      </span>
                    </span>
                    <span className="flex items-center space-x-2">
                      <i className="fas fa-clock text-primary"></i>
                      <span className="font-medium">
                        수정: {formatDate(notice.updatedAt)}
                      </span>
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-400">#{notice.id}</span>
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="bg-white rounded-xl shadow-md p-16 text-center">
            <i className="fas fa-inbox text-8xl mb-6 text-gray-200"></i>
            <p className="text-xl font-semibold text-gray-600 mb-2">
              {searchTerm || filterStatus !== "all" ? "검색 결과가 없습니다" : "등록된 공지사항이 없습니다"}
            </p>
            <p className="text-gray-400">
              {searchTerm || filterStatus !== "all"
                ? "다른 검색어나 필터를 시도해보세요"
                : "새 공지사항을 작성해보세요"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
